import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { usePilabTriageGuideline } from '../../../hooks/usePilabRequests.js';

/**
 * 振り分けの基準を画面でも読めるようにする。
 *
 * **出す人と AI が同じ文書を読む。** 基準が AI の中にしか無いと、却下されたときに
 * 納得できず、同じ要望が形を変えて出てくる。正本はリポジトリの `guidelines/triage.md` で、
 * API がそれをそのまま配るので、ここに本文を写さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望の振り分け基準を画面で開いて読めるようにする
 * @graph-connects usePilabTriageGuideline [calls] 振り分け基準の取得
 */
export function TriageGuidelinePanel(): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError } = usePilabTriageGuideline({ enabled: open });

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('improvements.guideline.show')}
      </Button>
    );
  }

  return (
    <Card>
      <CardContent>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
          }}
        >
          {t('improvements.guideline.hide')}
        </Button>
        {isLoading && (
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            {t('improvements.guideline.loading')}
          </p>
        )}
        {isError && (
          <p className="mt-2 text-sm text-[var(--color-error)]">
            {t('improvements.guideline.error')}
          </p>
        )}
        {data !== undefined && (
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-xs text-[var(--color-text-primary)]">
            {data.guideline}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}
